import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import Card from '@/components/ui/Card'
import { cn } from '@/lib/cn'

export type StatTone = 'primary' | 'success' | 'danger' | 'warning' | 'pending' | 'neutral'

/* Icewall icon chips: the same tinted recipe as buttons and badges. */
const tones: Record<StatTone, string> = {
  primary: 'border-primary/60 bg-primary/20 text-primary',
  success: 'border-success/60 bg-success/20 text-success',
  danger: 'border-danger/60 bg-danger/20 text-danger',
  warning: 'border-warning/60 bg-warning/20 text-warning',
  pending: 'border-pending/60 bg-pending/20 text-pending',
  neutral: 'border-foreground/30 bg-foreground/10 text-foreground/80',
}

/**
 * The dashboard's KPI tile: round icon chip, small label, big value. Wraps
 * itself in a Link when `to` is given so a number can lead to its list.
 */
export default function StatTile({
  label,
  value,
  icon,
  tone = 'primary',
  to,
  hint,
  className,
}: {
  label: string
  value: ReactNode
  icon: ReactNode
  tone?: StatTone
  to?: string
  hint?: ReactNode
  className?: string
}) {
  const card = (
    <Card className={cn('h-full', to && 'transition-shadow hover:shadow-md', className)}>
      <div className="flex items-center gap-3">
        <span className={cn('flex size-10 shrink-0 items-center justify-center rounded-full border', tones[tone])}>{icon}</span>
        <div className="min-w-0">
          <p className="truncate text-xs text-foreground/60">{label}</p>
          <p className="text-xl font-medium text-foreground">{value}</p>
          {hint && <p className="text-2xs text-muted">{hint}</p>}
        </div>
      </div>
    </Card>
  )

  return to ? (
    <Link to={to} className="block">
      {card}
    </Link>
  ) : (
    card
  )
}
